import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { membresService } from '../../services/api.service';
import PageHeader from '../common/PageHeader';

const ValidationMembres = () => {
  const [membresEnAttente, setMembresEnAttente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'valider' ou 'rejeter'
  const [raisonRejet, setRaisonRejet] = useState('');
  const [processing, setProcessing] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadMembresEnAttente();
  }, []);

  const loadMembresEnAttente = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await membresService.getEnAttente();
      const membres = response.data?.data || response.data || [];
      console.log(`✅ ${membres.length} membres en attente`);
      setMembresEnAttente(membres);
    } catch (err) {
      console.error('❌ Erreur chargement:', err);
      setError('Erreur lors du chargement des membres en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (membre, type) => {
    setSelectedMembre(membre);
    setDialogType(type);
    setDialogOpen(true);
    setRaisonRejet('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMembre(null);
    setDialogType('');
    setRaisonRejet('');
  };

  const handleValider = async () => {
    if (!selectedMembre) return;

    setProcessing(true);
    try {
      await membresService.validerMembre(selectedMembre.id);
      setSuccess(`Compte de ${selectedMembre.prenom} ${selectedMembre.nom} validé avec succès`);
      handleCloseDialog();
      await loadMembresEnAttente();
    } catch (err) {
      console.error('❌ Erreur validation:', err);
      setError('Erreur lors de la validation du compte');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejeter = async () => {
    if (!selectedMembre) return;

    setProcessing(true);
    try {
      await membresService.rejeterMembre(selectedMembre.id, raisonRejet);
      setSuccess(`Compte de ${selectedMembre.prenom} ${selectedMembre.nom} rejeté`);
      handleCloseDialog();
      await loadMembresEnAttente();
    } catch (err) {
      console.error('❌ Erreur rejet:', err);
      setError('Erreur lors du rejet du compte');
    } finally {
      setProcessing(false);
    }
  };

  const handleShowDetails = (membre) => {
    setSelectedMembre(membre);
    setDetailsDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Validation des Comptes"
        subtitle="Gérer les demandes d'inscription en attente"
        showLogo={false}
        showDecoration={false}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D4AF37', mb: 1 }}>
                {membresEnAttente.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Demandes en attente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {membresEnAttente.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Aucune demande en attente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Toutes les demandes d'inscription ont été traitées
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Membre</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date Inscription</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {membresEnAttente.map((membre) => (
                <TableRow 
                  key={membre.id}
                  sx={{ 
                    '&:hover': { bgcolor: 'grey.50' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#D4AF37' }}>
                        {membre.prenom?.[0]}{membre.nom?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {membre.prenom} {membre.nom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Matricule: {membre.matricule}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      {membre.telephone}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      {membre.email || '-'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      {formatDate(membre.created_at)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Voir les détails">
                        <IconButton
                          size="small"
                          onClick={() => handleShowDetails(membre)}
                          sx={{ color: '#1976d2' }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Valider le compte">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(membre, 'valider')}
                          sx={{ color: '#4caf50' }}
                        >
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rejeter la demande">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(membre, 'rejeter')}
                          sx={{ color: '#f44336' }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Validation/Rejet */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: dialogType === 'valider' ? '#4caf50' : '#f44336', color: 'white' }}>
          {dialogType === 'valider' ? 'Valider le compte' : 'Rejeter la demande'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedMembre && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {dialogType === 'valider' 
                  ? `Êtes-vous sûr de vouloir valider le compte de ${selectedMembre.prenom} ${selectedMembre.nom} ?`
                  : `Êtes-vous sûr de vouloir rejeter la demande de ${selectedMembre.prenom} ${selectedMembre.nom} ?`
                }
              </Typography>

              {dialogType === 'valider' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Le membre recevra une notification WhatsApp et pourra se connecter immédiatement.
                </Alert>
              )}

              {dialogType === 'rejeter' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Raison du rejet (optionnel)"
                  value={raisonRejet}
                  onChange={(e) => setRaisonRejet(e.target.value)}
                  placeholder="Ex: Informations incomplètes, doublon, etc."
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={dialogType === 'valider' ? handleValider : handleRejeter}
            disabled={processing}
            sx={{
              bgcolor: dialogType === 'valider' ? '#4caf50' : '#f44336',
              '&:hover': {
                bgcolor: dialogType === 'valider' ? '#45a049' : '#da190b'
              }
            }}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détails */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a1a1a', color: '#D4AF37' }}>
          Détails du membre
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedMembre && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: '#D4AF37', 
                    mx: 'auto', 
                    mb: 2,
                    fontSize: '2rem'
                  }}
                >
                  {selectedMembre.prenom?.[0]}{selectedMembre.nom?.[0]}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {selectedMembre.prenom} {selectedMembre.nom}
                </Typography>
                <Chip 
                  label="En attente" 
                  color="warning" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Matricule
                    </Typography>
                    <Typography variant="body1">
                      {selectedMembre.matricule}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Téléphone
                    </Typography>
                    <Typography variant="body1">
                      {selectedMembre.telephone}
                    </Typography>
                  </Box>
                </Box>

                {selectedMembre.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {selectedMembre.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedMembre.date_naissance && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date de naissance
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedMembre.date_naissance)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedMembre.adresse && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Adresse
                      </Typography>
                      <Typography variant="body1">
                        {selectedMembre.adresse}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date d'inscription
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedMembre.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Fermer</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => {
              setDetailsDialogOpen(false);
              handleOpenDialog(selectedMembre, 'rejeter');
            }}
          >
            Rejeter
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => {
              setDetailsDialogOpen(false);
              handleOpenDialog(selectedMembre, 'valider');
            }}
            sx={{
              background: 'linear-gradient(45deg, #D4AF37 30%, #F4E5B8 90%)',
              color: '#000'
            }}
          >
            Valider
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationMembres;
